import { useMemo, useState } from 'react'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/shared/lib/utils'

export type PackagingDocumentLibraryPickerRow = {
  id: string
  fileName: string
  documentType: string
  packagingItemCount: number
}

type PackagingDocumentLibraryPickerLabels = {
  sectionTitle: string
  searchPlaceholder: string
  emptyLibrary: string
  emptySearch: string
  linkedItemCount: string
  attaching: string
}

type PackagingDocumentLibraryPickerProps = {
  rows: PackagingDocumentLibraryPickerRow[]
  typeLabelForRow?: (documentType: string) => string
  labels: PackagingDocumentLibraryPickerLabels
  /** When `rows` is empty, shown instead of `labels.emptyLibrary` (e.g. all files already linked). */
  emptyListMessage?: string
  disabled?: boolean
  isAttaching?: boolean
  onSelect: (libraryDocumentId: string) => void
}

export function PackagingDocumentLibraryPicker({
  rows,
  typeLabelForRow,
  labels,
  emptyListMessage,
  disabled = false,
  isAttaching = false,
  onSelect,
}: PackagingDocumentLibraryPickerProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return rows
    }
    return rows.filter((row) => row.fileName.toLowerCase().includes(query))
  }, [rows, searchQuery])

  if (rows.length === 0) {
    const listEmptyMessage = emptyListMessage ?? labels.emptyLibrary
    return (
      <div className='rounded-lg border border-dashed p-4 text-sm text-muted-foreground'>
        {listEmptyMessage}
      </div>
    )
  }

  return (
    <div className='min-w-0 space-y-2'>
      <div className='flex min-w-0 flex-wrap items-center justify-between gap-2'>
        <p className='text-sm font-medium'>{labels.sectionTitle}</p>
        {isAttaching && (
          <p className='text-xs text-muted-foreground'>{labels.attaching}</p>
        )}
      </div>
      <Input
        value={searchQuery}
        onChange={(event) => {
          setSearchQuery(event.target.value)
        }}
        placeholder={labels.searchPlaceholder}
        disabled={disabled || isAttaching}
      />
      <ul
        className={cn(
          'max-h-44 min-w-0 space-y-1 overflow-y-auto overflow-x-hidden rounded-md border p-1',
          (disabled || isAttaching) && 'pointer-events-none opacity-60'
        )}
      >
        {filteredRows.length === 0 && (
          <li className='px-2 py-3 text-center text-sm text-muted-foreground'>
            {labels.emptySearch}
          </li>
        )}
        {filteredRows.map((row) => {
          const typeLabel = typeLabelForRow?.(row.documentType)
          const linkedCountLabel = labels.linkedItemCount.replace(
            '{{count}}',
            String(row.packagingItemCount)
          )
          return (
            <li key={row.id} className='min-w-0'>
              <Button
                type='button'
                variant='ghost'
                className='h-auto w-full min-w-0 shrink justify-start gap-2 overflow-hidden whitespace-normal px-2 py-2 text-start'
                disabled={disabled || isAttaching}
                onClick={() => {
                  onSelect(row.id)
                }}
              >
                <FileText className='size-4 shrink-0 text-muted-foreground' />
                <span className='min-w-0 flex-1 overflow-hidden'>
                  <span
                    className='block break-all text-sm font-medium'
                    title={row.fileName}
                  >
                    {row.fileName}
                  </span>
                  <span className='block text-xs text-muted-foreground'>
                    {typeLabel != null && typeLabel.length > 0
                      ? `${typeLabel} · ${linkedCountLabel}`
                      : linkedCountLabel}
                  </span>
                </span>
              </Button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
